import React, { useEffect, useRef, useState } from 'react';
import MediaBox from './MediaBox';

export type GalleryItem = { url: string; type: 'image' | 'video' };

/** True when a post carries more than one photo/video and should use the gallery. */
export function hasMultiMedia(post: { media?: GalleryItem[] | null }): boolean {
  return (post.media?.length || 0) > 1;
}

interface Props {
  media: GalleryItem[];
  poster?: string | null;
  postId?: string;
  maxHeight?: number | string;
}

const navBtn: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  fontSize: 20,
  lineHeight: '36px',
  cursor: 'pointer',
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const PostMediaGallery: React.FC<Props> = ({ media, poster, postId, maxHeight }) => {
  const [idx, setIdx] = useState(0);
  const touchX = useRef<number | null>(null);
  const count = media.length;
  const current = Math.min(idx, count - 1);
  const item = media[current];
  if (!item) return null;

  const go = (delta: number) => setIdx((i) => Math.max(0, Math.min(count - 1, i + delta)));
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <div
      style={{ position: 'relative' }}
      onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        const end = e.changedTouches[0]?.clientX;
        if (start == null || end == null || Math.abs(end - start) < 40) return;
        go(end < start ? 1 : -1);
      }}
    >
      <MediaBox
        key={item.url}
        src={item.url}
        type={item.type}
        alt={`Post media ${current + 1} of ${count}`}
        poster={item.type === 'video' && current === 0 ? poster : undefined}
        controls
        contain={item.type === 'video'}
        maxHeight={maxHeight ?? '60vh'}
        watchContent={item.type === 'video' && postId ? { type: 'post', id: postId } : undefined}
      />
      {count > 1 && (
        <>
          <span
            style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, zIndex: 3 }}
          >
            {current + 1} / {count}
          </span>
          {current > 0 && (
            <button type="button" aria-label="Previous" style={{ ...navBtn, left: 8 }} onClick={(e) => { stop(e); go(-1); }}>‹</button>
          )}
          {current < count - 1 && (
            <button type="button" aria-label="Next" style={{ ...navBtn, right: 8 }} onClick={(e) => { stop(e); go(1); }}>›</button>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '8px 0 2px' }}>
            {media.map((m, i) => (
              <button
                key={m.url}
                type="button"
                aria-label={`Show item ${i + 1}`}
                onClick={(e) => { stop(e); setIdx(i); }}
                style={{ width: 8, height: 8, padding: 0, borderRadius: '50%', border: 'none', cursor: 'pointer', background: i === current ? '#0B6B6F' : 'rgba(128,128,128,0.45)' }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PostMediaGallery;

/** Small square preview of a locally selected file, with a remove button. */
export const SelectedMediaThumb: React.FC<{ file: File; onRemove: () => void; label?: string; large?: boolean }> = ({ file, onRemove, label, large }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const isVideo = file.type.startsWith('video') || /\.(mp4|mov|webm|m4v|mkv|3gp)$/i.test(file.name);
  const box: React.CSSProperties = large
    ? { width: 'auto', maxWidth: 'min(320px, 80vw)', height: 'auto', maxHeight: 240, objectFit: 'contain', borderRadius: 10, display: 'block', background: '#000' }
    : { width: 72, height: 72, objectFit: 'cover', borderRadius: 8, display: 'block', background: '#000' };
  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }} title={file.name}>
      {url && (isVideo
        ? <video src={large ? url : `${url}#t=0.1`} muted playsInline preload="metadata" controls={large} style={box} />
        : <img src={url} alt={file.name} style={box} />)}
      {isVideo && !large && <span style={{ position: 'absolute', bottom: 4, left: 4, color: '#fff', fontSize: 12, textShadow: '0 0 3px #000' }}>▶</span>}
      {label && <span style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, borderRadius: 6, padding: '0 5px' }}>{label}</span>}
      <button
        type="button"
        aria-label={`Remove ${file.name}`}
        onClick={onRemove}
        style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', border: '2px solid #fff', background: '#c0392b', color: '#fff', fontSize: 12, lineHeight: '16px', padding: 0, cursor: 'pointer' }}
      >
        ✕
      </button>
    </div>
  );
};
